// ClipRadar website — demo scenes modeled on the REAL Premiere Pro PANEL UI
// (live captures in design-reference/app-captures/, taken from the running app
// in panel mode). Palette, chips, placement bar, cards, highlights and the
// transcript view all mirror the shipping panel.
import { Fragment, useEffect, useState, type CSSProperties, type ReactElement, type ReactNode } from 'react';
import { CRWIcon, type IconName } from './icons';
import { Thumb } from './primitives';
import { STILLS, FACES } from './stills';
import { useTyping, useSettled } from './hooks';
import logoMarkMono from '../assets/logo-mark-mono.svg';

// Real app palette (extracted from the live panel DOM)
export const APP = {
  bg: '#0A0A0B', panel: '#0F0F10', surface: '#171717', raised: '#1F1F22', active: '#27272A',
  line: 'rgba(255,255,255,0.08)', line2: 'rgba(255,255,255,0.13)',
  text: '#DEDEDE', mut: '#A1A1A1', dim: '#6E6E76',
  blue: '#3368D0', focus: 'rgba(96,158,250,0.55)', blueText: '#7FA8F8', mark: 'rgba(86,134,244,0.30)',
  green: '#00D492', check: '#10B981', yellow: '#FFD60A',
  chip: 'rgba(39,39,42,0.6)', chipBorder: 'rgba(255,255,255,0.1)',
};

const mono = 'var(--font-mono)';

/* ================= Premiere dock frame ================= */
// The panel lives docked inside Premiere — slim panel-tab strip on top,
// panel content below, wrapped in the marketing gradient ring.
export function PanelDock({ children, width = 620 }: { children: ReactNode; width?: number }) {
  const settled = useSettled();
  return (
    <div style={{ height: '100%', display: 'flex', justifyContent: 'center' }}>
      <div style={{ height: '100%', width: '100%', maxWidth: width, borderRadius: 14, padding: 1, background: 'linear-gradient(160deg, rgba(255,255,255,0.18), rgba(255,255,255,0.04) 30%, rgba(var(--light-soft-rgb),0.25) 70%, rgba(255,255,255,0.06))', boxShadow: '0 30px 80px rgba(0,0,0,0.6), 0 0 90px rgba(var(--light-rgb),0.07)' }}>
        <div data-settled={settled ? 'true' : undefined} style={{ display: 'flex', flexDirection: 'column', height: '100%', borderRadius: 13, overflow: 'hidden', background: APP.bg }}>
          {/* Premiere panel-tab strip */}
          <div style={{ flex: 'none', display: 'flex', alignItems: 'stretch', gap: 2, padding: '5px 8px 0', background: '#1D1D21', borderBottom: '1px solid rgba(0,0,0,0.55)' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 12px 6px', borderRadius: '6px 6px 0 0', background: APP.bg, fontSize: 10.5, fontWeight: 600, color: APP.text, position: 'relative' }}>
              <img src={logoMarkMono} width="10" height="10" alt="" style={{ opacity: 0.85 }} />
              ClipRadar
              <i style={{ position: 'absolute', left: 10, right: 10, top: 0, height: 2, borderRadius: 2, background: APP.focus.replace('0.55', '0.9') }}></i>
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', padding: '4px 12px 6px', fontSize: 10.5, color: '#6e6e78' }}>Effects</span>
            <span style={{ flex: 1 }}></span>
            <span style={{ display: 'inline-flex', alignItems: 'center', color: '#6e6e78', fontSize: 12, paddingRight: 4 }}>≡</span>
          </div>
          <div style={{ flex: 1, minHeight: 0, position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>{children}</div>
        </div>
      </div>
    </div>
  );
}

/* ================= Panel chrome pieces ================= */
export function PanelTabs({ tab = 'Search' }: { tab?: string }) {
  const tabs: [IconName, string][] = [['search', 'Search'], ['film', 'Media'], ['users', 'People'], ['gear', 'Settings'], ['help', 'Help']];
  return (
    <div className="crw-panel-tabs" style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 3, padding: '7px 10px 6px' }}>
      {tabs.map(([ic, l]) => {
        const on = l === tab;
        return (
          <span key={l} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 9px', borderRadius: 7, fontSize: 10.5, fontWeight: on ? 600 : 500, color: on ? APP.text : APP.mut, background: on ? APP.active : 'transparent', border: `1px solid ${on ? 'rgba(255,255,255,0.15)' : 'transparent'}` }}>
            <CRWIcon d={ic} size={10.5} /> {l}
          </span>
        );
      })}
      <span style={{ flex: 1 }}></span>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 500, color: APP.green, flex: 'none' }}>
        <i style={{ width: 5, height: 5, borderRadius: '50%', background: APP.green, display: 'inline-block' }}></i>
        <span className="crw-online-label">Online</span>
      </span>
    </div>
  );
}

function ModeToggle({ mode = 'Visuals' }: { mode?: string }) {
  const modes: [IconName, string][] = [['image', 'Visuals'], ['mic', 'Speech']];
  return (
    <div style={{ display: 'inline-flex', gap: 2, padding: 2, borderRadius: 9, background: APP.panel, border: `1px solid ${APP.line}` }}>
      {modes.map(([ic, m]) => {
        const on = m === mode;
        return (
          <span key={m} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 7, fontSize: 10.5, fontWeight: on ? 600 : 500, color: on ? APP.text : APP.dim, background: on ? APP.active : 'transparent', border: `1px solid ${on ? 'rgba(255,255,255,0.12)' : 'transparent'}` }}>
            <CRWIcon d={ic} size={10} /> {m}
          </span>
        );
      })}
    </div>
  );
}

// Scope chips — dashed borders like the real app; `active` = the chip whose
// popover is open (aria-expanded:bg-muted in the real app).
function ScopeChip({ icon, label, on, blue, active }: { icon: IconName; label: string; on?: boolean; blue?: boolean; active?: boolean }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 9px', borderRadius: 6, fontSize: 10, fontWeight: 500, color: blue ? '#9EC2FF' : on === false ? APP.dim : APP.text, background: blue ? 'rgba(51,104,208,0.18)' : active ? APP.active : APP.chip, border: blue ? '1px solid rgba(96,158,250,0.45)' : active ? `1px solid ${APP.line2}` : `1px dashed ${APP.chipBorder}` }}>
      <CRWIcon d={icon} size={10} />{label}
    </span>
  );
}

// The real panel search box: dashed scope chips, query row with ✕ + camera + blue
// submit. `instant` = the query was searched BEFORE this scene starts — full text,
// no typing, no caret.
function PanelSearchBox({ query, delay = 500, person, personImg, imageRef, focus = true, placeholder, instant, scopeLabel, scopeActive, peopleLabel, peopleActive }: { query?: string; delay?: number; person?: string; personImg?: string; imageRef?: boolean; focus?: boolean; placeholder?: string; instant?: boolean; scopeLabel?: string; scopeActive?: boolean; peopleLabel?: string; peopleActive?: boolean }) {
  const typing = useTyping(instant ? '' : query || '', { delay });
  const typed = instant ? query || '' : typing.typed;
  const done = instant ? true : typing.done;
  const lit = focus && (query ? done : true);
  return (
    <div style={{ borderRadius: 11, background: APP.panel, border: '1px solid', borderColor: lit ? APP.focus : APP.chipBorder, boxShadow: lit ? '0 0 0 3px rgba(96,158,250,0.12), 0 0 24px rgba(96,158,250,0.10)' : 'none', transition: 'border-color 300ms var(--ease-out), box-shadow 300ms var(--ease-out)', padding: '8px 9px', display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
        <ScopeChip icon="at" label={scopeLabel ?? 'All analyzed media'} active={scopeActive} />
        <ScopeChip icon="user" label={peopleLabel ?? 'People'} on={!!person || !!peopleLabel} active={peopleActive} />
        <ScopeChip icon="ban" label="Exclude" on={false} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
        {imageRef ? (
          <span className="crw-pop" style={{ animationDelay: `${delay}ms`, display: 'inline-flex', alignItems: 'center', gap: 6, padding: 2, paddingRight: 8, borderRadius: 6, background: 'rgba(51,104,208,0.18)', border: '1px solid rgba(96,158,250,0.45)' }}>
            <Thumb img={STILLS.freewaycars} pos="50% 45%" h={20} style={{ width: 34, borderRadius: 4 }} />
            <span style={{ fontFamily: mono, fontSize: 9, color: '#9EC2FF' }}>reference.jpg</span>
          </span>
        ) : null}
        {person ? (
          <span className="crw-pop" style={{ animationDelay: `${delay - 100}ms`, display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 8px 2px 3px', borderRadius: 999, background: 'rgba(51,104,208,0.18)', border: '1px solid rgba(96,158,250,0.45)' }}>
            <FaceDot name={person} img={personImg ?? FACES.jfk[0]} size={16} />
            <span style={{ fontSize: 9.5, color: '#9EC2FF', fontWeight: 500 }}>{person}</span>
          </span>
        ) : null}
        <span style={{ flex: 1, minWidth: 0, fontSize: 12, color: typed ? APP.text : APP.dim, whiteSpace: 'nowrap', overflow: 'hidden' }}>
          {typed || placeholder || (query ? '' : 'Search your footage…')}{query && !done ? <span className="crw-caret"></span> : null}
        </span>
        {typed ? <span style={{ color: APP.dim, display: 'inline-flex', flex: 'none' }}><CRWIcon d="x" size={11} /></span> : null}
        <span style={{ width: 26, height: 26, flex: 'none', borderRadius: 7, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: imageRef ? '1px solid rgba(96,158,250,0.6)' : `1px solid ${APP.chipBorder}`, color: imageRef ? '#9EC2FF' : APP.mut, background: imageRef ? 'rgba(51,104,208,0.18)' : 'transparent' }}><CRWIcon d="camera" size={12} /></span>
        <span style={{ width: 28, height: 26, flex: 'none', borderRadius: 7, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: APP.blue, color: '#F2F9FF', boxShadow: '0 2px 8px rgba(51,104,208,0.4)' }}><CRWIcon d="arrowRight" size={12} sw={2.2} /></span>
      </div>
    </div>
  );
}

function HistoryRow({ chips }: { chips: string[] }) {
  return (
    <div style={{ display: 'flex', gap: 5, alignItems: 'center', marginTop: 6 }}>
      {chips.map((c) => (
        <span key={c} style={{ fontSize: 9, color: APP.mut, padding: '2px 8px', borderRadius: 999, border: '1px solid rgba(255,255,255,0.06)', whiteSpace: 'nowrap' }}>{c}</span>
      ))}
      <span style={{ flex: 1 }}></span>
      <span style={{ fontSize: 9.5, color: APP.mut, fontWeight: 500 }}>Clear</span>
    </div>
  );
}

// Placement bar — the 3 placement-mode icons + zoom + Place / Clear all.
export function PlacementBar({ active = 0, placeLit }: { active?: number; placeLit?: boolean }) {
  const modes: [IconName, string][] = [['wand', 'Smart'], ['downToLine', 'Insert'], ['toLine', 'Append']];
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
      <span style={{ display: 'inline-flex', gap: 2, padding: 2, borderRadius: 8, border: `1px solid ${APP.line}` }}>
        {modes.map(([ic, label], i) => (
          <span key={label} title={label} style={{ width: 24, height: 22, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: 6, color: i === active ? APP.text : APP.dim, background: i === active ? APP.active : 'transparent', border: `1px solid ${i === active ? 'rgba(255,255,255,0.12)' : 'transparent'}` }}>
            <CRWIcon d={ic} size={11} />
          </span>
        ))}
      </span>
      <span className="crw-zoom" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: APP.dim }}>
        <CRWIcon d="grid" size={11} />
        <span style={{ width: 64, height: 3, borderRadius: 2, background: 'rgba(255,255,255,0.12)', position: 'relative' }}>
          <i style={{ position: 'absolute', left: '38%', top: -3.5, width: 10, height: 10, borderRadius: '50%', background: '#D4D4D8' }}></i>
        </span>
      </span>
      <span style={{ flex: 1 }}></span>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 11px', borderRadius: 7, fontSize: 10.5, fontWeight: 600, color: placeLit ? '#F2F9FF' : APP.dim, background: placeLit ? APP.blue : 'transparent', border: placeLit ? '1px solid transparent' : `1px solid ${APP.line}`, boxShadow: placeLit ? '0 2px 10px rgba(51,104,208,0.4)' : 'none' }}>
        <CRWIcon d="plus" size={10} sw={2.4} /> Place
      </span>
      <span className="crw-clearall" style={{ padding: '4px 10px', borderRadius: 7, fontSize: 10.5, fontWeight: 500, color: APP.mut, border: `1px solid ${APP.line}`, whiteSpace: 'nowrap' }}>Clear all results</span>
    </div>
  );
}

// Result-group header — “query” in N files + collapse / copy / hide. A
// Match-similar block shows the SEED THUMBNAIL instead of a query string
// (result-blocks.tsx renders block.similarThumbnailUrl as a size-7 rounded img).
function GroupHeader({ q, thumb, n = 4, delay = 1500 }: { q?: string; thumb?: string; n?: number; delay?: number }) {
  return (
    <div className="crw-fade" style={{ animationDelay: `${delay}ms`, display: 'flex', alignItems: 'center', gap: 6 }}>
      {thumb ? (
        <span style={{ width: 24, height: 24, flex: 'none', borderRadius: 5, overflow: 'hidden', border: `1px solid ${APP.line2}` }}>
          <img src={thumb} alt="" loading="lazy" decoding="async" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        </span>
      ) : null}
      <span style={{ fontFamily: mono, fontSize: 10, color: APP.text, fontWeight: 500, flex: '1 1 auto', minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{q ? <>“{q}” </> : null}<span style={{ color: APP.dim, fontWeight: 400 }}>in {n} {n === 1 ? 'file' : 'files'}</span></span>
      <span style={{ color: APP.dim, display: 'inline-flex', flex: 'none' }}><CRWIcon d="chevdown" size={11} /></span>
      <span style={{ flex: 'none', fontSize: 9, color: APP.mut, padding: '3px 8px', borderRadius: 6, border: `1px solid ${APP.chipBorder}`, display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}><CRWIcon d="layers" size={9} /> Copy 10 to clipboard</span>
      <span className="crw-eyebtn" style={{ flex: 'none', width: 22, height: 22, borderRadius: 6, border: `1px solid ${APP.line}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: APP.dim }}><CRWIcon d="eye" size={11} /></span>
    </div>
  );
}

// Blue inline highlight (search-result match) — yellow is reserved for the transcript view.
function Mk({ children, yellow }: { children: ReactNode; yellow?: boolean }) {
  return <mark style={{ background: yellow ? APP.yellow : APP.mark, color: yellow ? '#1A1A00' : APP.text, borderRadius: 3, padding: '0 2px', fontWeight: yellow ? 500 : 400 }}>{children}</mark>;
}

type HighlightPart = [string] | [string, boolean];
function Highlighted({ parts, size = 11, yellow }: { parts: HighlightPart[]; size?: number; yellow?: boolean }) {
  return (
    <span style={{ fontSize: size, lineHeight: 1.55, color: APP.mut }}>
      {parts.map(([t, hl], i) => hl ? <Mk key={i} yellow={yellow}>{t}</Mk> : <Fragment key={i}>{t}</Fragment>)}
    </span>
  );
}

// Glassy select checkbox (matches the real card checkbox).
function CardCheck({ on }: { on?: boolean }) {
  return (
    <span style={{ position: 'absolute', top: 5, left: 5, width: 12, height: 12, borderRadius: 4, border: on ? '1px solid transparent' : '1px solid rgba(255,255,255,0.55)', background: on ? 'linear-gradient(135deg, #58AAF6, #4F46E5)' : 'linear-gradient(135deg, rgba(255,255,255,0.25), rgba(255,255,255,0))', boxShadow: on ? '0 0 8px rgba(80,120,240,0.8)' : 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
      {on ? <CRWIcon d="check" size={8} sw={3.5} /> : null}
    </span>
  );
}

// The 5-button hover strip (Place · Source monitor · In/Out · Bin · Match similar).
// `pressAt` flashes a click on the hot button at that delay.
function FiveBtns({ delay = 0, hot, pressAt }: { delay?: number; hot?: number; pressAt?: number }) {
  const icons: IconName[] = ['plus', 'monitor', 'minus', 'folder', 'search'];
  return (
    <div className="crw-fade" style={{ animationDelay: `${delay}ms`, display: 'flex', gap: 3, flex: 1, alignItems: 'center' }}>
      {icons.map((ic, i) => (
        <span key={i} className={i === hot && pressAt != null ? 'crw-press' : undefined} style={{ animationDelay: i === hot && pressAt != null ? `${pressAt}ms` : undefined, flex: 1, height: 22, borderRadius: 6, border: `1px solid ${i === hot ? 'rgba(96,158,250,0.6)' : APP.line}`, background: i === hot ? 'rgba(51,104,208,0.2)' : APP.raised, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: i === hot ? '#9EC2FF' : APP.mut }}>
          {ic === 'minus' ? <span style={{ fontFamily: mono, fontSize: 8.5, letterSpacing: 1 }}>[ ]</span> : <CRWIcon d={ic} size={11} />}
        </span>
      ))}
    </div>
  );
}

// Panel visual result card — real-frame thumb + glass checkbox + duration, then
// filename + blue timecode. On hover the action strip REPLACES the meta rows
// (like the real app) — the card never grows. `hover` = static hover state;
// `hoverAt` choreographs it: the border lights, the meta fades to the strip,
// and (with `pressAt`) the hot button flashes a click.
function PanelCard({ img, pos, zoom, title, tin, tout, dur, delay, hover, hoverAt, pressAt, hot = 4, checked, style }: { img: string; pos?: string; zoom?: number; title: string; tin: string; tout: string; dur: string; delay: number; hover?: boolean; hoverAt?: number; pressAt?: number; hot?: number; checked?: boolean; style?: CSSProperties }) {
  const lit = hover && hoverAt == null;
  return (
    <div className={hoverAt != null ? 'crw-hoverlit' : 'crw-pop'} style={{ animationDelay: `${hoverAt ?? delay}ms`, borderRadius: 8, overflow: 'hidden', background: APP.surface, border: `1px solid ${lit ? 'rgba(96,158,250,0.65)' : APP.line}`, boxShadow: lit ? '0 0 14px rgba(80,130,240,0.25)' : 'none', ...style }}>
      <div style={{ position: 'relative' }}>
        <Thumb img={img} pos={pos} zoom={zoom} delay={delay} style={{ borderRadius: 0, border: 'none', boxShadow: 'none' }} />
        <CardCheck on={checked} />
        <span style={{ position: 'absolute', right: 4, bottom: 4, fontFamily: mono, fontSize: 8, fontWeight: 600, color: '#fff', background: 'rgba(0,0,0,0.78)', padding: '1px 5px', borderRadius: 4 }}>{dur}</span>
      </div>
      <div style={{ position: 'relative', height: 33, padding: '0 7px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        {hover && hoverAt == null ? (
          <FiveBtns delay={delay + 500} hot={hot} />
        ) : (
          <>
            <div className={hoverAt != null ? 'crw-swapout' : undefined} style={{ animationDelay: hoverAt != null ? `${hoverAt}ms` : undefined }}>
              <div style={{ fontSize: 9.5, fontWeight: 500, color: APP.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</div>
              <div style={{ fontFamily: mono, fontSize: 8, color: APP.blueText, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden' }}>{tin} - {tout}</div>
            </div>
            {hoverAt != null ? (
              <div style={{ position: 'absolute', inset: '0 7px', display: 'flex', alignItems: 'center' }}>
                <FiveBtns delay={hoverAt + 220} hot={hot} pressAt={pressAt} />
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

// Panel speech row — thumb with timecode badge, highlighted text, ＋ [ ] ≡ actions.
function SpeechRow({ img, pos, zoom, t, file, parts, delay, hotAction }: { img: string; pos?: string; zoom?: number; t: string; file: string; parts: HighlightPart[]; delay: number; hotAction?: number }) {
  const actions = ['plus', 'brackets', 'lines'];
  return (
    <div className="crw-pop" style={{ animationDelay: `${delay}ms`, display: 'flex', gap: 9, padding: 8, borderRadius: 9, background: APP.surface, border: `1px solid ${APP.line}` }}>
      <div style={{ position: 'relative', flex: 'none' }}>
        <Thumb img={img} pos={pos} zoom={zoom} h={56} style={{ width: 96 }} />
        <span style={{ position: 'absolute', left: 4, bottom: 4, fontFamily: mono, fontSize: 7.5, fontWeight: 600, color: '#fff', background: 'rgba(0,0,0,0.78)', padding: '1px 4px', borderRadius: 3 }}>{t}</span>
        <CardCheck />
      </div>
      <div style={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Highlighted parts={parts} />
        <div style={{ display: 'flex', gap: 6, marginTop: 'auto', paddingTop: 4, alignItems: 'center' }}>
          <span style={{ fontFamily: mono, fontSize: 8.5, color: APP.blueText, flex: 'none' }}>{t}</span>
          <span style={{ fontSize: 8.5, color: APP.dim, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: '0 1 auto', minWidth: 0 }}>· {file}</span>
          <span style={{ flex: '1 0 4px' }}></span>
          {actions.map((a, k) => (
            <span key={a} style={{ width: 22, height: 20, flex: 'none', borderRadius: 5, border: `1px solid ${k === hotAction ? 'rgba(96,158,250,0.6)' : APP.chipBorder}`, background: k === hotAction ? 'rgba(51,104,208,0.2)' : 'transparent', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: k === hotAction ? '#9EC2FF' : APP.mut }}>
              {a === 'plus' ? <CRWIcon d="plus" size={10} sw={2.4} /> : a === 'brackets' ? <span style={{ fontFamily: mono, fontSize: 8, letterSpacing: 1 }}>[ ]</span> : <CRWIcon d="text" size={10} />}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

export function FaceDot({ name, img, size = 24 }: { name: string; img: string; size?: number }) {
  return (
    <span title={name} style={{ width: size, height: size, borderRadius: '50%', flex: 'none', overflow: 'hidden', display: 'inline-flex', border: '1px solid rgba(255,255,255,0.18)' }}>
      <img src={img} alt={name} loading="lazy" decoding="async" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
    </span>
  );
}

// Standard panel body padding
function Body({ children, pad = '8px 12px 0' }: { children: ReactNode; pad?: string }) {
  return <div style={{ flex: 1, minHeight: 0, padding: pad, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>{children}</div>;
}

// Results region container (rounded group like the real app). flex:none —
// inside the fixed-height panel body, overflowing blocks must spill past the
// bottom edge (like scrolled-out content), never get squashed by flex.
function GroupBox({ children, delay = 1500 }: { children: ReactNode; delay?: number }) {
  return (
    <div className="crw-fade" style={{ animationDelay: `${delay}ms`, flex: 'none', marginTop: 8, borderRadius: 9, background: 'rgba(23,23,23,0.5)', border: `1px solid ${APP.chipBorder}`, padding: '8px 9px' }}>
      {children}
    </div>
  );
}

// The library is the app's REAL archival demo pack (NASA / National Archives /
// Prelinger, PD-safe) — the same clips recur across scenes the way a real
// library would, and every result is a TRUE match for its query (archival
// re-skin, founder 2026-07-17). Speech lines are verbatim from speech.sqlite.
type Clip = { img: string; title: string; pos?: string; zoom?: number };
// the moonwalk reel — three distinct moments, dead-on matches
const MOONWALK: Clip[] = [
  { img: STILLS.moonwalkers, title: 'apollo11-moonwalk-1969.mp4' },
  { img: STILLS.flagplant, title: 'apollo11-moonwalk-1969.mp4' },
  { img: STILLS.moonwalk2, title: 'apollo11-moonwalk-1969.mp4' },
];
// the launch reel — one pad, many scenes (per-still filenames match the hero)
const LAUNCH: Clip[] = [
  { img: STILLS.launchpad, title: 'apollo11-pad-39a.mp4' },
  { img: STILLS.padflame, title: 'apollo11-launch-1969.mp4' },
  { img: STILLS.ignition, title: 'apollo11-liftoff-1969.mp4' },
];

/* ================= VISUAL ================= */
function SceneVisualSearch() {
  const tins = ['00:00:52', '00:00:28', '00:00:02'];
  const touts = ['00:01:06', '00:00:37', '00:00:06'];
  return (
    <PanelDock>
      <PanelTabs />
      <Body>
        <div style={{ marginBottom: 7 }}><ModeToggle /></div>
        <PanelSearchBox query="astronauts working on the lunar surface" />
        <HistoryRow chips={['the rocket on the pad', 'kennedy at the podium']} />
        <PlacementBar />
        <GroupBox delay={1900}>
          <GroupHeader q="astronauts working on the lunar…" n={1} delay={1900} />
          <div className="crw-scene-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 7, marginTop: 7 }}>
            {MOONWALK.map((c, i) => (
              <PanelCard key={i} img={c.img} pos={['50% 40%', '50% 45%', '40% 40%'][i]} title={c.title} tin={tins[i]} tout={touts[i]} dur={['14s', '9s', '4s'][i]} delay={2100 + i * 140} />
            ))}
          </div>
        </GroupBox>
      </Body>
    </PanelDock>
  );
}

// Segmented tab strip (the real popover's bordered tablist).
function SegTabs({ tabs, on, style }: { tabs: string[]; on: string; style?: CSSProperties }) {
  return (
    <div style={{ display: 'flex', gap: 2, padding: 2, borderRadius: 7, border: `1px solid ${APP.line2}`, ...style }}>
      {tabs.map((t) => (
        <span key={t} style={{ flex: 1, textAlign: 'center', padding: '3px 8px', borderRadius: 5, fontSize: 9.5, fontWeight: 500, color: t === on ? APP.text : APP.dim, background: t === on ? APP.active : 'transparent' }}>{t}</span>
      ))}
    </div>
  );
}

// Checkbox row inside a popover checklist (checkbox + name + count).
function CheckRow({ name, count, on, d = 0 }: { name: string; count?: string; on?: boolean; d?: number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '4px 6px', borderRadius: 5 }}>
      <span className={on && d ? 'crw-pop' : ''} style={{ animationDelay: `${d}ms`, width: 12, height: 12, borderRadius: 3, flex: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: on ? 'none' : '1px solid rgba(255,255,255,0.28)', background: on ? APP.blue : 'transparent', color: '#fff' }}>
        {on ? <CRWIcon d="check" size={8} sw={3.5} /> : null}
      </span>
      <span style={{ fontSize: 10.5, color: APP.text, minWidth: 0, flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</span>
      {count ? <span style={{ fontFamily: mono, fontSize: 8.5, color: APP.dim, flex: 'none' }}>{count}</span> : null}
    </div>
  );
}

// The REAL scope popover (frontend/src/features/search/scope-popover.tsx): panel
// Library⇄Project switch, Search-all toggle, tabs + filter + checklist, footer.
function SceneContextPicker() {
  const rows = [
    { name: 'Apollo 11 — NASA reels', count: '412', on: true, d: 900 },
    { name: 'JFK Library — addresses', count: '386', on: true, d: 1400 },
    { name: 'Universal Newsreels 1961–63', count: '1,892', on: false, d: 0 },
    { name: 'Prelinger — ephemeral films', count: '406', on: true, d: 1900 },
  ];
  return (
    <PanelDock>
      <PanelTabs />
      <Body>
        <div style={{ marginBottom: 7 }}><ModeToggle /></div>
        <div style={{ position: 'relative' }}>
        <PanelSearchBox focus={false} placeholder="Search your footage…" scopeLabel="3 included" scopeActive />
        {/* the scope popover — anchored right under the @ CHIP, overlapping the query row */}
        <div className="crw-pop" style={{ animationDelay: '400ms', position: 'absolute', top: 32, left: 10, width: 312, zIndex: 3, borderRadius: 10, background: APP.raised, border: `1px solid ${APP.line2}`, boxShadow: '0 18px 50px rgba(0,0,0,0.65)', overflow: 'hidden' }}>
        {/* Scope: Library ⇄ Project (panel only) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 11px', borderBottom: `1px solid ${APP.line}` }}>
          <span style={{ fontSize: 9.5, fontWeight: 500, color: APP.mut, flex: 'none' }}>Scope</span>
          <div style={{ display: 'flex', flex: 1, gap: 2, padding: 2, borderRadius: 7, border: `1px solid ${APP.line2}` }}>
            <span style={{ flex: 1, textAlign: 'center', padding: '3px 8px', borderRadius: 5, fontSize: 9.5, fontWeight: 500, color: APP.text, background: 'rgba(51,104,208,0.15)', border: '1px solid rgba(96,158,250,0.4)' }}>Library</span>
            <span style={{ flex: 1, textAlign: 'center', padding: '3px 8px', borderRadius: 5, fontSize: 9.5, fontWeight: 500, color: APP.dim, border: '1px solid transparent' }}>Project</span>
          </div>
        </div>
        {/* Search all analyzed media — toggle OFF (we're picking) */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '8px 11px', borderBottom: `1px solid ${APP.line}` }}>
          <span style={{ fontSize: 10.5, fontWeight: 500, color: APP.text }}>Search all analyzed media</span>
          <span style={{ position: 'relative', width: 30, height: 17, borderRadius: 999, background: APP.active, flex: 'none' }}>
            <i style={{ position: 'absolute', left: 2, top: 2, width: 13, height: 13, borderRadius: '50%', background: '#D4D4D8' }}></i>
          </span>
        </div>
        <div style={{ padding: 10 }}>
          <SegTabs tabs={['Collections', 'Folders', 'Media']} on="Folders" />
          <div style={{ margin: '8px 0', padding: '4px 8px', borderRadius: 6, border: `1px solid ${APP.line2}`, fontSize: 9.5, color: APP.dim }}>Select media to include in search...</div>
          <div style={{ borderRadius: 7, border: `1px solid ${APP.line}`, padding: 3, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {rows.map((r) => <CheckRow key={r.name} {...r} />)}
          </div>
          <div style={{ display: 'flex', gap: 6, marginTop: 9, paddingTop: 8, borderTop: `1px solid ${APP.line}` }}>
            <span style={{ fontSize: 9.5, fontWeight: 500, color: APP.text, padding: '3px 9px', borderRadius: 6, border: `1px solid ${APP.line2}` }}>Select all</span>
            <span style={{ fontSize: 9.5, fontWeight: 500, color: APP.mut, padding: '3px 9px', borderRadius: 6 }}>Deselect all</span>
          </div>
        </div>
        </div>
        </div>
      </Body>
    </PanelDock>
  );
}

function SceneImageSearch() {
  return (
    <PanelDock>
      <PanelTabs />
      <Body>
        <div style={{ marginBottom: 7 }}><ModeToggle /></div>
        <PanelSearchBox query="" imageRef delay={600} focus={false} />
        <PlacementBar />
        <GroupBox delay={1400}>
          <GroupHeader q="reference.jpg" n={2} delay={1400} />
          <div className="crw-scene-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 7, marginTop: 7 }}>
            {[
              { img: STILLS.freeway, title: 'freeway-driving-1950s.mp4', pos: '50% 50%', tin: '00:00:08', tout: '00:00:12', dur: '4s' },
              { img: STILLS.freeway3, title: 'freeway-driving-1950s.mp4', pos: '50% 45%', tin: '00:00:19', tout: '00:00:22', dur: '3s' },
              { img: STILLS.underpass, title: 'la-freeway-survey-1956.mp4', pos: '50% 45%', tin: '00:00:04', tout: '00:00:09', dur: '5s' },
            ].map((c, i) => (
              <PanelCard key={i} img={c.img} pos={c.pos} zoom={1.04} title={c.title} tin={c.tin} tout={c.tout} dur={c.dur} delay={1600 + i * 140} />
            ))}
          </div>
        </GroupBox>
      </Body>
    </PanelDock>
  );
}

// Match similar, the way it actually happens — a played-back recording:
// the search ALREADY ran (query sits in the box, results below), the cursor
// hovers a card (border lights, meta swaps to the action strip), the magnifier
// flashes a CLICK, and the new similar block EXPANDS at the top, pushing the
// original block down (use-result-blocks.ts prepends; the header carries the
// seed thumbnail instead of a query — result-blocks.tsx).
function SceneMatchSimilar() {
  const HOVER = 900, PRESS = 1650, OPEN = 2050;
  return (
    <PanelDock>
      <PanelTabs />
      <Body>
        <div style={{ marginBottom: 7 }}><ModeToggle /></div>
        <PanelSearchBox query="the rocket on the launch pad" instant focus={false} />
        <PlacementBar />
        {/* the click spawns this block — it expands open and shoves the old one
            down PAST the panel's bottom edge (the panel is a fixed viewport;
            after the push the old block is only partially visible, like real
            content scrolled out of view) */}
        <div className="crw-expand" style={{ animationDelay: `${OPEN}ms`, flex: 'none' }}>
          <GroupBox delay={OPEN}>
            <GroupHeader thumb={STILLS.launchpad} n={2} delay={OPEN + 150} />
            <div className="crw-scene-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 7, marginTop: 7 }}>
              {[
                { img: STILLS.padsmoke, title: 'apollo11-launch-1969.mp4', pos: '50% 45%', tin: '00:00:26', tout: '00:00:30', dur: '4s' },
                { img: STILLS.climb, title: 'apollo11-liftoff-1969.mp4', pos: '50% 40%', tin: '00:00:33', tout: '00:00:36', dur: '3s' },
                { img: STILLS.tracking, title: 'apollo11-tracking-cam.mp4', pos: '50% 40%', tin: '00:00:39', tout: '00:00:44', dur: '5s' },
              ].map((c, i) => (
                <PanelCard key={i} img={c.img} pos={c.pos} title={c.title} tin={c.tin} tout={c.tout} dur={c.dur} delay={OPEN + 250 + i * 130} />
              ))}
            </div>
          </GroupBox>
        </div>
        {/* the original block — fully visible until the click, then pushed out */}
        <GroupBox delay={0}>
          <GroupHeader q="the rocket on the launch pad" n={3} delay={0} />
          <div className="crw-scene-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 7, marginTop: 7 }}>
            {LAUNCH.map((c, i) => (
              <PanelCard key={i} img={c.img} pos={['50% 45%', '50% 45%', '50% 50%'][i]} title={c.title} tin={['00:00:05', '00:00:21', '00:00:24'][i]} tout={['00:00:12', '00:00:24', '00:00:29'][i]} dur={['7s', '3s', '5s'][i]} delay={0} hoverAt={i === 0 ? HOVER : undefined} pressAt={i === 0 ? PRESS : undefined} hot={4} />
            ))}
          </div>
        </GroupBox>
      </Body>
    </PanelDock>
  );
}

/* ================= SPEECH ================= */
function SceneSpeechSearch() {
  return (
    <PanelDock>
      <PanelTabs />
      <Body>
        <div style={{ marginBottom: 7 }}><ModeToggle mode="Speech" /></div>
        <PanelSearchBox query={'"go to the moon"'} />
        <PlacementBar />
        {/* both lines verbatim from the clips' own transcripts (speech.sqlite):
            rice seg 2 @ 18.5s; press-conf seg 9 @ 74.8s — a cross-file match */}
        <GroupBox delay={2000}>
          <GroupHeader q="go to the moon" n={2} delay={2000} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 7 }}>
            <SpeechRow img={STILLS.ricespeech} pos="50% 30%" t="00:00:18" file="jfk-moon-rice-1962.mp4" delay={2200}
              parts={[['…why does Rice play Texas? We choose to '], ['go to the moon', true], ['. We choose to '], ['go to the moon', true], ['…']]} />
            <SpeechRow img={STILLS.astronautsdesk} pos="50% 40%" zoom={1.05} t="00:01:14" file="apollo11-press-conference-1969.mp4" delay={2400}
              parts={[['…we were certainly on the right track when we took this commitment to '], ['go to the moon', true], ['.']]} />
          </div>
        </GroupBox>
      </Body>
    </PanelDock>
  );
}

// The panel transcript — full-panel takeover (filename header, yellow marks,
// selected row with blue ring, 5-icon toolbar).
function SceneTranscript() {
  // six consecutive REAL rows from jfk-moon-rice-1962.mp4 (speech.sqlite segs
  // 0–5, real segment starts; clip runs 75s). Yellow marks = the "go to the
  // moon" search carried in from the speech scene.
  const lines: { t: string; parts: HighlightPart[]; sel?: boolean }[] = [
    { t: '00:00:00', parts: [['may never come again. But why some say the moon? Why choose this as our goal?…']] },
    { t: '00:00:10', parts: [['why climb the highest mountain? Why, 35 years ago, fly the Atlantic? Why does Rice play Texas?']] },
    { t: '00:00:18', parts: [['We choose to ', false], ['go to the moon', true], ['. We choose to ', false], ['go to the moon', true], ['. We choose to ', false], ['go to the moon', true], [' in this']], sel: true },
    { t: '00:00:31', parts: [['decade and do the other things, not because they are easy, but because they are hard.']] },
    { t: '00:00:38', parts: [['Because that goal will serve to organize and measure the best of our energies and skills.']] },
    { t: '00:00:46', parts: [['Because that challenge is one that we’re willing to accept, one we are unwilling to postpone…']] },
  ];
  return (
    <PanelDock>
      <div style={{ flex: 'none', display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 13px 8px' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontFamily: mono, fontSize: 11, fontWeight: 700, color: APP.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>jfk-moon-rice-1962.mp4</div>
          <div style={{ fontFamily: mono, fontSize: 8.5, color: APP.dim, marginTop: 2 }}>00:01:15</div>
        </div>
        <span style={{ flex: 1 }}></span>
        <span style={{ color: APP.mut, display: 'inline-flex', paddingTop: 2 }}><CRWIcon d="x" size={13} /></span>
      </div>
      <div style={{ flex: 'none', padding: '0 13px 8px' }}>
        <div style={{ borderRadius: 8, border: `1px solid ${APP.chipBorder}`, padding: '5px 10px', fontSize: 10, color: APP.dim }}>Search in this transcript…</div>
        <div className="crw-fade" style={{ animationDelay: '1800ms', fontFamily: mono, fontSize: 8.5, color: APP.mut, marginTop: 6 }}>Selected: 00:00:18 – 00:00:31 <span style={{ color: APP.dim }}>(1 row)</span></div>
      </div>
      <div style={{ flex: 1, minHeight: 0, padding: '0 13px', display: 'flex', flexDirection: 'column', gap: 1, overflow: 'hidden', borderTop: `1px solid ${APP.line}` }}>
        <div style={{ paddingTop: 6 }}></div>
        {lines.map((l, i) => (
          <div key={i} style={{ display: 'flex', gap: 9, padding: '4px 7px', borderRadius: 8, position: 'relative', alignItems: 'baseline' }}>
            {l.sel ? <span className="crw-fade" style={{ animationDelay: '1100ms', position: 'absolute', inset: 0, borderRadius: 8, background: 'rgba(51,104,208,0.12)', border: '1px solid rgba(96,158,250,0.55)', boxShadow: '0 0 10px rgba(80,130,240,0.15)' }}></span> : null}
            <span style={{ fontFamily: mono, fontSize: 8.5, color: l.sel ? APP.blueText : APP.dim, flex: 'none', position: 'relative' }}>{l.t}</span>
            <span style={{ position: 'relative' }}><Highlighted parts={l.parts} yellow size={10.5} /></span>
          </div>
        ))}
      </div>
      <div style={{ flex: 'none', display: 'flex', gap: 5, padding: '9px 13px', borderTop: `1px solid ${APP.line}` }}>
        {(['sparkle', 'download', 'toLine'] as IconName[]).map((ic) => (
          <span key={ic} style={{ width: 26, height: 24, borderRadius: 6, border: `1px solid ${APP.chipBorder}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: APP.mut }}><CRWIcon d={ic} size={11} /></span>
        ))}
        <span style={{ width: 26, height: 24, borderRadius: 6, border: `1px solid ${APP.chipBorder}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: APP.mut, fontFamily: mono, fontSize: 8.5, letterSpacing: 1 }}>[ ]</span>
        <span style={{ width: 26, height: 24, borderRadius: 6, border: `1px solid ${APP.chipBorder}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: APP.mut, fontFamily: mono, fontSize: 8 }}>CC</span>
        <span style={{ flex: 1 }}></span>
        <span className="crw-pop" style={{ animationDelay: '2300ms', display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 11px', borderRadius: 7, background: APP.blue, color: '#F2F9FF', fontSize: 10, fontWeight: 600, boxShadow: '0 2px 10px rgba(51,104,208,0.4)' }}>
          <CRWIcon d="plus" size={10} sw={2.5} /> Add to timeline
        </span>
      </div>
    </PanelDock>
  );
}

// Track-header gutter cell — label + lock, like Premiere's track controls.
function TrackHead({ label }: { label: string }) {
  return (
    <span style={{ width: 30, flex: 'none', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
      <span style={{ fontFamily: mono, fontSize: 7, color: APP.dim }}>{label}</span>
      <span style={{ color: APP.dim, opacity: 0.5, display: 'inline-flex' }}><CRWIcon d="lock" size={6} /></span>
    </span>
  );
}

// Tiny waveform fill for audio clips.
function Wave({ n = 22 }: { n?: number }) {
  return (
    <span style={{ position: 'absolute', inset: '2px 4px', display: 'flex', alignItems: 'center', gap: 1 }}>
      {Array.from({ length: n }, (_, i) => (
        <i key={i} style={{ flex: 1, height: `${26 + ((i * 41) % 56)}%`, borderRadius: 1, background: 'rgba(34,197,94,0.5)' }}></i>
      ))}
    </span>
  );
}

export function MiniTimeline({ dropDelay = 1200, dropLabel = 'JFK_RICE · 0:18–0:31' }: { dropDelay?: number | null; dropLabel?: string }) {
  const showDrop = dropDelay != null && dropDelay < 9000;
  const clip = (l: string, w: string, hue: number, label?: string): ReactNode => (
    <span style={{ position: 'absolute', left: l, width: w, top: 1.5, bottom: 1.5, borderRadius: 3, background: `linear-gradient(180deg, oklch(0.34 0.04 ${hue}), oklch(0.28 0.04 ${hue}))`, border: '1px solid rgba(255,255,255,0.1)', overflow: 'hidden' }}>
      {label ? <i style={{ position: 'absolute', left: 4, top: 1, fontStyle: 'normal', fontFamily: mono, fontSize: 6.5, color: 'rgba(255,255,255,0.55)', whiteSpace: 'nowrap' }}>{label}</i> : null}
    </span>
  );
  return (
    <div style={{ borderRadius: 8, background: 'rgba(0,0,0,0.5)', border: `1px solid ${APP.line}`, padding: '7px 10px 8px' }}>
      {/* ruler with tick marks */}
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <span style={{ width: 34, flex: 'none', fontFamily: mono, fontSize: 7, color: APP.blueText }}>00:08:21:07</span>
        <div className="crw-ruler" style={{ flex: 1, display: 'flex', overflow: 'hidden', borderBottom: '1px solid rgba(255,255,255,0.07)', paddingBottom: 2 }}>
          {Array.from({ length: 9 }, (_, i) => (
            <span key={i} style={{ flex: 1, fontFamily: mono, fontSize: 7, color: APP.dim, whiteSpace: 'nowrap', borderLeft: '1px solid rgba(255,255,255,0.09)', paddingLeft: 3 }}>{String(i * 2).padStart(2, '0')}:00</span>
          ))}
        </div>
      </div>
      {/* tracks + playhead overlay */}
      <div style={{ position: 'relative', marginTop: 5 }}>
        {/* V2 — empty, thin */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 3 }}>
          <TrackHead label="V2" />
          <div style={{ position: 'relative', flex: 1, height: 10, borderRadius: 3, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.035)' }}></div>
        </div>
        {/* V1 — picture */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 3 }}>
          <TrackHead label="V1" />
          <div style={{ position: 'relative', flex: 1, height: 24, borderRadius: 3, background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.04)' }}>
            {clip('0%', '21%', 250, 'INAUGURATION')}
            {clip('22%', '13%', 270, 'PAD_39A')}
            <span className={showDrop ? 'crw-drop' : ''} style={{ animationDelay: showDrop ? `${dropDelay}ms` : undefined, visibility: showDrop ? 'visible' : 'hidden', position: 'absolute', left: '36%', width: '26%', top: 1.5, bottom: 1.5, borderRadius: 3, background: 'rgba(51,104,208,0.3)', border: `1px solid ${APP.blue}`, boxShadow: '0 0 14px rgba(51,104,208,0.35)', display: 'inline-flex', alignItems: 'center', overflow: 'hidden' }}>
              <span style={{ fontFamily: mono, fontSize: 7.5, color: '#9EC2FF', padding: '0 5px', whiteSpace: 'nowrap' }}>{dropLabel}</span>
            </span>
            {clip('63%', '17%', 200, 'MOONWALK')}
          </div>
        </div>
        {/* A1 — audio with waveforms */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <TrackHead label="A1" />
          <div style={{ position: 'relative', flex: 1, height: 14, borderRadius: 3, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.035)' }}>
            <span style={{ position: 'absolute', left: 0, width: '35%', top: 1.5, bottom: 1.5, borderRadius: 3, background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.3)', overflow: 'hidden' }}><Wave /></span>
            <span className={showDrop ? 'crw-drop' : ''} style={{ animationDelay: showDrop ? `${dropDelay + 150}ms` : undefined, visibility: showDrop ? 'visible' : 'hidden', position: 'absolute', left: '36%', width: '26%', top: 1.5, bottom: 1.5, borderRadius: 3, background: 'rgba(34,197,94,0.18)', border: '1px solid rgba(34,197,94,0.5)', overflow: 'hidden' }}><Wave n={14} /></span>
            <span style={{ position: 'absolute', left: '63%', width: '17%', top: 1.5, bottom: 1.5, borderRadius: 3, background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.3)', overflow: 'hidden' }}><Wave n={12} /></span>
          </div>
        </div>
        {/* playhead with handle, spanning all tracks */}
        <span aria-hidden="true" style={{ position: 'absolute', left: 'calc(34px + (100% - 34px) * 0.36)', top: -7, bottom: 0, width: 1.5, background: APP.blue, boxShadow: '0 0 6px rgba(51,104,208,0.6)', zIndex: 2, pointerEvents: 'none' }}>
          <i style={{ position: 'absolute', top: 0, left: -3.25, borderLeft: '4px solid transparent', borderRight: '4px solid transparent', borderTop: `5px solid ${APP.blue}` }}></i>
        </span>
      </div>
    </div>
  );
}

function SceneAddTimeline() {
  return (
    <PanelDock>
      <PanelTabs />
      <Body>
        <div style={{ marginTop: 2 }}>
          <SpeechRow img={STILLS.ricespeech} pos="50% 30%" zoom={1.08} t="00:00:18" file="jfk-moon-rice-1962.mp4" delay={200} hotAction={0}
            parts={[['…'], ['We choose to go to the moon', true], [' in this decade and do the other things…']]} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '10px 2px' }}>
          <PlacementBar placeLit />
        </div>
        <MiniTimeline dropDelay={1400} />
        <div className="crw-fade" style={{ animationDelay: '2200ms', fontFamily: mono, fontSize: 9, color: APP.green, letterSpacing: '0.06em', marginTop: 10 }}>✓ PLACED — IN/OUT PRESERVED, NO OVERWRITE</div>
      </Body>
    </PanelDock>
  );
}

function SceneLanguages() {
  // Two stories in one scene: foreign-language footage genuinely transcribed
  // in its own language (the Spanish rows are real lines from the
  // Spanish-narrated films, verified against design-reference/transcripts/)
  // AND ask-in-your-language cross-language search for the rest — every
  // non-Spanish query below means "we chose to go to the moon" and lands on
  // the clips' actual English transcripts (speech.sqlite, verbatim).
  const qs: [string, string][] = [
    ['Български', 'решихме да отидем до луната'],
    ['日本語', '月に行くことを選んだ'],
    ['Español', 'productos hechos en Cuba'],
    ['العربية', 'اخترنا الذهاب إلى القمر'],
  ];
  const moonRow: { img: string; pos: string; t: string; file: string; parts: HighlightPart[] } = { img: STILLS.ricespeech, pos: '50% 30%', t: '00:00:18', file: 'jfk-moon-rice-1962.mp4', parts: [['We choose to '], ['go to the moon', true], ['. We choose to go to the moon…']] };
  const commitmentRow: { img: string; pos: string; t: string; file: string; parts: HighlightPart[] } = { img: STILLS.astronautsdesk, pos: '50% 40%', t: '00:01:14', file: 'apollo11-press-conference-1969.mp4', parts: [['…we took this commitment to '], ['go to the moon', true], ['.']] };
  const rowsByLang: { img: string; pos: string; t: string; file: string; parts: HighlightPart[] }[][] = [
    [moonRow, commitmentRow],
    [
      { img: STILLS.ricehands, pos: '50% 30%', t: '00:00:00', file: 'jfk-moon-rice-1962.mp4', parts: [['But why some say '], ['the moon', true], ['? Why choose this as our goal?']] },
      { img: STILLS.pressdesk2, pos: '50% 40%', t: '00:00:00', file: 'apollo11-press-conference-1969.mp4', parts: [['…the meaning of this first '], ['landing', true], [' on another body in space.']] },
    ],
    [
      { img: STILLS.cubashop, pos: '50% 40%', t: '00:01:52', file: 'adelante-cubanos-1959.mp4', parts: [['La mujer cubana va de tiendas y se fija con gusto en artículos con la etiqueta '], ['hecho en Cuba', true], ['.']] },
      { img: STILLS.cubasalon, pos: '50% 35%', t: '00:03:58', file: 'adelante-cubanos-part2-1959.mp4', parts: [['…hoy se registra una '], ['Cuba productora', true], [' de artículos de belleza para la mujer.']] },
    ],
    [moonRow, commitmentRow],
  ];
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % qs.length), 2100);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const cur = qs[idx];
  const rows = rowsByLang[idx];
  return (
    <PanelDock>
      <PanelTabs />
      <Body>
        <div style={{ marginBottom: 7 }}><ModeToggle mode="Speech" /></div>
        <div style={{ borderRadius: 11, background: APP.panel, border: `1px solid ${APP.focus}`, boxShadow: '0 0 0 3px rgba(96,158,250,0.12)', padding: '8px 9px', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ color: APP.dim, display: 'inline-flex' }}><CRWIcon d="globe" size={12} /></span>
          <span key={idx} className="crw-fade" style={{ fontSize: 12, color: APP.text, flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden' }}>{cur[1]}</span>
          <span key={`b${idx}`} className="crw-fade" style={{ fontFamily: mono, fontSize: 8.5, color: '#9EC2FF', background: 'rgba(51,104,208,0.18)', border: '1px solid rgba(96,158,250,0.45)', padding: '2px 8px', borderRadius: 999, whiteSpace: 'nowrap' }}>{cur[0]}</span>
          <span style={{ width: 28, height: 26, flex: 'none', borderRadius: 7, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: APP.blue, color: '#F2F9FF' }}><CRWIcon d="arrowRight" size={12} sw={2.2} /></span>
        </div>
        <div key={`r${idx}`} style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 9 }}>
          {rows.map((r, i) => (
            <SpeechRow key={i} img={r.img} pos={r.pos} zoom={1.05 + i * 0.05} t={r.t} file={r.file} delay={400 + i * 250} parts={r.parts} />
          ))}
        </div>
        <div style={{ display: 'flex', gap: 5, marginTop: 9, flexWrap: 'wrap' }}>
          {qs.map((q, i) => (
            <span key={i} style={{ fontFamily: mono, fontSize: 8, padding: '3px 8px', borderRadius: 999, border: '1px solid', borderColor: i === idx ? 'rgba(96,158,250,0.5)' : APP.line, color: i === idx ? '#9EC2FF' : APP.dim, background: i === idx ? 'rgba(51,104,208,0.18)' : 'transparent', transition: 'border-color 300ms var(--ease-out), color 300ms var(--ease-out), background-color 300ms var(--ease-out)' }}>{q[0]}</span>
          ))}
        </div>
      </Body>
    </PanelDock>
  );
}

/* ================= FACES ================= */
function SceneFacesSearch() {
  return (
    <PanelDock>
      <PanelTabs />
      <Body>
        <div style={{ marginBottom: 7 }}><ModeToggle /></div>
        <PanelSearchBox query="answering questions at the press conference" person="Neil Armstrong" personImg={FACES.armstrong[0]} delay={900} />
        <PlacementBar />
        {/* the only bare-faced Armstrong in the archive IS the press conference
            — helmeted moonwalk shots are never face results (no face to match) */}
        <GroupBox delay={2300}>
          <GroupHeader q="Neil Armstrong · answering questions" n={1} delay={2300} />
          <div className="crw-scene-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 7, marginTop: 7 }}>
            {[
              { img: STILLS.astronautsdesk, pos: '50% 40%', tin: '00:00:03', tout: '00:00:16', dur: '13s' },
              { img: STILLS.pressdesk3, pos: '50% 40%', tin: '00:00:20', tout: '00:00:29', dur: '9s' },
              { img: STILLS.pressdesk2, pos: '50% 40%', tin: '00:00:48', tout: '00:00:57', dur: '9s' },
            ].map((c, i) => (
              <PanelCard key={i} img={c.img} pos={c.pos} title="apollo11-press-conference-1969.mp4" tin={c.tin} tout={c.tout} dur={c.dur} delay={2500 + i * 140} />
            ))}
          </div>
        </GroupBox>
      </Body>
    </PanelDock>
  );
}

// The REAL people filter (frontend/src/features/search/people-popover.tsx):
// the dashed People chip reads "2 any people", its popover holds an Any/All
// segmented toggle + the named-people checklist; results swap with the mode.
function SceneFacesCombine() {
  const [mode, setMode] = useState<'all' | 'any'>('any');
  useEffect(() => {
    const t = setInterval(() => setMode((m) => (m === 'all' ? 'any' : 'all')), 2800);
    return () => clearInterval(t);
  }, []);
  return (
    <PanelDock>
      <PanelTabs />
      <Body>
        <div style={{ marginBottom: 7 }}><ModeToggle /></div>
        <div style={{ position: 'relative' }}>
          <PanelSearchBox focus={false} placeholder="Search your footage…" peopleLabel={`2 ${mode} people`} peopleActive />
          {/* people popover — anchored right under the People CHIP (it overlaps the
              query row, like the real app), so the result faces below stay visible */}
          <div className="crw-pop" style={{ animationDelay: '300ms', position: 'absolute', top: 32, left: 122, width: 200, zIndex: 3, borderRadius: 10, background: APP.raised, border: `1px solid ${APP.line2}`, boxShadow: '0 18px 50px rgba(0,0,0,0.65)', overflow: 'hidden' }}>
            <div style={{ display: 'flex', gap: 2, padding: 6, borderBottom: `1px solid ${APP.line}` }}>
              {(['any', 'all'] as const).map((m) => (
                <span key={m} style={{ flex: 1, textAlign: 'center', padding: '3px 8px', borderRadius: 5, fontSize: 10, fontWeight: 500, color: mode === m ? APP.text : APP.dim, background: mode === m ? APP.active : 'transparent', transition: 'color 250ms var(--ease-out), background-color 250ms var(--ease-out)' }}>{m === 'any' ? 'Any' : 'All'}</span>
              ))}
            </div>
            <div style={{ padding: 6, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <CheckRow name="John F. Kennedy" on d={700} />
              <CheckRow name="Neil Armstrong" />
              <CheckRow name="Earl Warren" on d={500} />
            </div>
          </div>
        </div>
        <PlacementBar />
        {/* ALL = the oath — the one scene where Kennedy and Warren share the
            frame; ANY = every clip either appears in, across three files */}
        <GroupBox delay={300}>
          <GroupHeader key={`h${mode}`} q={mode === 'all' ? '2 all people' : '2 any people'} n={mode === 'all' ? 1 : 3} delay={0} />
          <div key={mode} className="crw-scene-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 7, marginTop: 7 }}>
            {(mode === 'all'
              ? [
                  { img: STILLS.oath, pos: '50% 35%', zoom: 1, title: 'jfk-inauguration-1961.mp4', tin: '00:00:00', tout: '00:00:09', dur: '9s' },
                  { img: STILLS.oath2, pos: '50% 35%', zoom: 1.06, title: 'jfk-inauguration-1961.mp4', tin: '00:00:13', tout: '00:00:21', dur: '8s' },
                  { img: STILLS.handshake, pos: '50% 35%', zoom: 1, title: 'jfk-inauguration-1961.mp4', tin: '00:00:27', tout: '00:00:31', dur: '4s' },
                ]
              : [
                  { img: STILLS.inauguration, pos: '50% 30%', zoom: 1, title: 'jfk-inauguration-1961.mp4', tin: '00:00:41', tout: '00:00:48', dur: '7s' },
                  { img: STILLS.cubaaddress, pos: '50% 35%', zoom: 1, title: 'jfk-cuba-address-1962.mp4', tin: '00:00:07', tout: '00:00:19', dur: '12s' },
                  { img: STILLS.ricespeech, pos: '50% 30%', zoom: 1, title: 'jfk-moon-rice-1962.mp4', tin: '00:00:18', tout: '00:00:32', dur: '14s' },
                ]
            ).map((c, i) => (
              <PanelCard key={i} img={c.img} pos={c.pos} zoom={c.zoom} title={c.title} tin={c.tin} tout={c.tout} dur={c.dur} delay={i * 120} />
            ))}
          </div>
        </GroupBox>
      </Body>
    </PanelDock>
  );
}

function SceneFacesName() {
  return (
    <PanelDock>
      <PanelTabs />
      <Body>
        <div style={{ marginBottom: 7 }}><ModeToggle /></div>
        <PanelSearchBox query="kennedy addressing the nation" />
        <div className="crw-pop" style={{ animationDelay: '2100ms', display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 8, padding: '4px 11px 4px 4px', borderRadius: 999, background: 'rgba(51,104,208,0.18)', border: '1px solid rgba(96,158,250,0.5)', alignSelf: 'flex-start' }}>
          <FaceDot name="John F. Kennedy" img={FACES.jfk[0]} size={18} />
          <span style={{ fontSize: 9, color: '#9EC2FF', fontFamily: mono }}>PERSON MATCHED: JOHN F. KENNEDY — 61 frames</span>
        </div>
        <GroupBox delay={2400}>
          <div className="crw-scene-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 7 }}>
            {[
              { img: STILLS.cubaaddress, title: 'jfk-cuba-address-1962.mp4', pos: '50% 35%', zoom: 1, tin: '00:00:07', tout: '00:00:19', dur: '12s' },
              { img: STILLS.inauguration, title: 'jfk-inauguration-1961.mp4', pos: '50% 30%', zoom: 1, tin: '00:00:41', tout: '00:00:48', dur: '7s' },
              { img: STILLS.ricespeech, title: 'jfk-moon-rice-1962.mp4', pos: '50% 30%', zoom: 1, tin: '00:00:18', tout: '00:00:32', dur: '14s' },
            ].map((c, i) => (
              <PanelCard key={i} img={c.img} pos={c.pos} zoom={c.zoom} title={c.title} tin={c.tin} tout={c.tout} dur={c.dur} delay={2400 + i * 140} />
            ))}
          </div>
        </GroupBox>
      </Body>
    </PanelDock>
  );
}

// Face cluster card — two real crops side-by-side, count badge, name / Add-name input.
function FaceCard({ name, imgs, count, delay, named, w = 96 }: { name?: string; imgs: readonly [string, string]; count: string; delay: number; named?: boolean; w?: number }) {
  return (
    <div className="crw-pop" style={{ animationDelay: `${delay}ms`, borderRadius: 8, overflow: 'hidden', background: APP.surface, border: `1px solid ${APP.line}`, width: w }}>
      <div style={{ display: 'flex', height: 46, position: 'relative' }}>
        {imgs.map((img, i) => (
          <span key={i} style={{ flex: 1, position: 'relative', overflow: 'hidden', borderLeft: i ? '1px solid rgba(0,0,0,0.4)' : 'none' }}>
            <img src={img} alt="" loading="lazy" decoding="async" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          </span>
        ))}
        <span style={{ position: 'absolute', left: 4, bottom: 4, display: 'inline-flex', alignItems: 'center', gap: 3, fontFamily: mono, fontSize: 7, color: '#fff', background: 'rgba(0,0,0,0.7)', padding: '1px 4px', borderRadius: 3 }}><CRWIcon d="image" size={7} /> {count}</span>
      </div>
      <div style={{ padding: '4px 7px 6px' }}>
        {named ? (
          <span style={{ fontSize: 9.5, fontWeight: 600, color: APP.text }}>{name}</span>
        ) : (
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ flex: 1, fontSize: 8, color: APP.dim, border: `1px solid ${APP.line}`, borderRadius: 5, padding: '2px 6px' }}>Add name…</span>
            <span style={{ color: APP.dim, fontSize: 9 }}>⋮</span>
          </span>
        )}
      </div>
    </div>
  );
}

function SceneFacesManage() {
  return (
    <PanelDock>
      <PanelTabs tab="People" />
      <Body pad="6px 12px 0">
        <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
          <span style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 10px', borderRadius: 8, background: APP.panel, border: `1px solid ${APP.chipBorder}`, fontSize: 10.5, color: APP.text }}>
            Everything · 3 <CRWIcon d="chevdown" size={10} />
          </span>
          <span style={{ width: 26, borderRadius: 8, border: `1px solid ${APP.chipBorder}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: APP.mut }}><CRWIcon d="plus" size={11} /></span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
          <b style={{ fontSize: 12, fontWeight: 700, color: APP.text }}>Everything</b>
          <span style={{ fontFamily: mono, fontSize: 8, color: APP.dim }}>(119 clusters, 742 faces)</span>
          <span style={{ flex: 1 }}></span>
          <span style={{ fontSize: 9, color: APP.dim, padding: '3px 9px', borderRadius: 6, border: `1px solid ${APP.line}` }}>Search by name…</span>
          <span style={{ fontSize: 9, color: APP.mut, padding: '3px 8px', borderRadius: 6, border: `1px solid ${APP.chipBorder}`, display: 'inline-flex', gap: 4, alignItems: 'center' }}><CRWIcon d="refresh" size={8} /> Refresh</span>
          <span style={{ fontSize: 9, fontWeight: 600, color: '#FF8A95', padding: '3px 8px', borderRadius: 6, background: 'rgba(255,84,104,0.12)', border: '1px solid rgba(255,84,104,0.3)' }}>Delete Unnamed</span>
        </div>
        <div style={{ fontSize: 9.5, fontWeight: 600, color: APP.mut, margin: '2px 0 5px' }}>Named People <span style={{ color: APP.dim, fontWeight: 400 }}>(4)</span></div>
        <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
          <FaceCard name="John F. Kennedy" imgs={FACES.jfk} count="61" delay={300} named w={104} />
          <FaceCard name="Neil Armstrong" imgs={FACES.armstrong} count="34" delay={420} named w={104} />
          <FaceCard name="Buzz Aldrin" imgs={FACES.aldrin} count="29" delay={540} named w={104} />
          <FaceCard name="Michael Collins" imgs={FACES.collins} count="27" delay={660} named w={104} />
        </div>
        <div style={{ fontSize: 9.5, fontWeight: 600, color: APP.mut, margin: '11px 0 5px' }}>Unnamed People <span style={{ color: APP.dim, fontWeight: 400 }}>(20 of 115)</span></div>
        <div className="crw-facegrid" style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
          {[FACES.u1, FACES.u2, FACES.u3, FACES.u4, FACES.u5].map((f, i) => <FaceCard key={i} imgs={f} count={String(17 - i * 2)} delay={600 + i * 110} w={88} />)}
        </div>
      </Body>
    </PanelDock>
  );
}

/* ================= HOW IT WORKS ================= */
function SceneIngest() {
  // real demo-library files with their real runtimes
  const files = [
    { n: 'apollo11-launch-1969.mp4', dur: '00:00:50', age: '1h', done: 4, d: 300 },
    { n: 'jfk-moon-rice-1962.mp4', dur: '00:01:15', age: '1h', done: 4, d: 600 },
    { n: 'mercury-redstone-launch-1960.mp4', dur: '00:14:35', age: '2d', done: 4, d: 900 },
    { n: 'adelante-cubanos-1959.mp4', dur: '00:10:17', age: '2d', done: 3, d: 1200 },
    { n: 'master-hands-factory-1936.mp4', dur: '00:00:50', age: '12d', done: 3, d: 1500 },
    { n: 'shy-guy-1947.mp4', dur: '00:13:35', age: '16d', done: 1, d: 1800 },
  ];
  const cols: IconName[] = ['search', 'image', 'mic', 'user'];
  return (
    <PanelDock>
      <PanelTabs tab="Media" />
      <Body pad="6px 12px 0">
        <div style={{ display: 'flex', gap: 6, marginBottom: 8, alignItems: 'center' }}>
          <span style={{ display: 'inline-flex', gap: 2, padding: 2, borderRadius: 8, background: APP.panel, border: `1px solid ${APP.line}` }}>
            {['Library', 'Project'].map((t, i) => (
              <span key={t} style={{ padding: '3px 11px', borderRadius: 6, fontSize: 10, fontWeight: i === 0 ? 600 : 500, color: i === 0 ? APP.text : APP.dim, background: i === 0 ? APP.active : 'transparent', border: `1px solid ${i === 0 ? 'rgba(255,255,255,0.12)' : 'transparent'}` }}>{t}</span>
            ))}
          </span>
          <span style={{ flex: 1 }}></span>
          <span style={{ width: 26, height: 24, borderRadius: 7, border: `1px solid ${APP.chipBorder}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: APP.mut }}><CRWIcon d="sliders" size={11} /></span>
          <span style={{ width: 26, height: 24, borderRadius: 7, border: `1px solid ${APP.chipBorder}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: APP.mut }}><CRWIcon d="refresh" size={11} /></span>
        </div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
          <span style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 10px', borderRadius: 8, background: APP.panel, border: `1px solid ${APP.chipBorder}`, fontSize: 10, color: APP.text }}>All media <CRWIcon d="chevdown" size={9} /></span>
          <span style={{ flex: 1.2, display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 8, background: APP.panel, border: `1px solid ${APP.chipBorder}`, fontSize: 10, color: APP.dim }}><CRWIcon d="search" size={10} /> Search media…</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '14px 1fr repeat(4, 18px) 58px 24px', gap: '0 6px', alignItems: 'center', padding: '0 8px 5px', borderBottom: `1px solid ${APP.line}` }}>
          <span style={{ width: 11, height: 11, borderRadius: 3, border: '1px solid rgba(255,255,255,0.22)' }}></span>
          <span style={{ fontFamily: mono, fontSize: 8, color: APP.dim, letterSpacing: '0.08em' }}>FILENAME ↑</span>
          {cols.map((c) => <span key={c} style={{ color: APP.dim, display: 'inline-flex', justifyContent: 'center' }}><CRWIcon d={c} size={9} /></span>)}
          <span style={{ fontFamily: mono, fontSize: 8, color: APP.dim, letterSpacing: '0.08em', textAlign: 'right' }}>DURATION</span>
          <span></span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {files.map((f, i) => (
            <div key={i} className="crw-fade" style={{ animationDelay: `${f.d}ms`, display: 'grid', gridTemplateColumns: '14px 1fr repeat(4, 18px) 58px 24px', gap: '0 6px', alignItems: 'center', padding: '6px 8px', borderBottom: `1px solid rgba(255,255,255,0.04)` }}>
              <span style={{ width: 11, height: 11, borderRadius: 3, border: '1px solid rgba(255,255,255,0.22)' }}></span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                <span style={{ color: APP.dim, display: 'inline-flex', flex: 'none' }}><CRWIcon d="film" size={10} /></span>
                <span style={{ fontSize: 10, color: APP.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.n}</span>
              </span>
              {[0, 1, 2, 3].map((k) => (
                <span key={k} style={{ display: 'inline-flex', justifyContent: 'center' }}>
                  <span className={k < f.done ? 'crw-pop' : ''} style={{ animationDelay: `${f.d + 400 + k * 220}ms`, width: 13, height: 13, borderRadius: 4, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: k < f.done ? APP.check : 'rgba(255,255,255,0.07)', color: '#fff' }}>
                    {k < f.done ? <CRWIcon d="check" size={8} sw={3.5} /> : null}
                  </span>
                </span>
              ))}
              <span style={{ fontFamily: mono, fontSize: 8.5, color: '#D8C66A', textAlign: 'right' }}>{f.dur}</span>
              <span style={{ fontFamily: mono, fontSize: 8.5, color: APP.dim }}>{f.age}</span>
            </div>
          ))}
        </div>
        <div className="crw-fade" style={{ animationDelay: '2400ms', display: 'flex', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
          {['TRANSCRIPT', 'SCENES', 'FACES', 'SEMANTIC'].map((s) => (
            <span key={s} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontFamily: mono, fontSize: 8, letterSpacing: '0.08em', color: APP.green }}><CRWIcon d="check" size={8} sw={2.5} /> {s}</span>
          ))}
          <span style={{ fontFamily: mono, fontSize: 8, letterSpacing: '0.06em', color: APP.dim }}>ALL LOCAL — NOTHING LEAVES THIS MACHINE</span>
        </div>
      </Body>
    </PanelDock>
  );
}

function SceneScope() {
  return (
    <PanelDock>
      <PanelTabs />
      <Body>
        <div style={{ marginBottom: 7 }}><ModeToggle /></div>
        <PanelSearchBox query="crowds gathered for the inauguration" />
        <PlacementBar />
        <GroupBox delay={1900}>
          <GroupHeader q="crowds gathered for the inauguration" n={2} delay={1900} />
          <div className="crw-scene-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 7, marginTop: 7 }}>
            {[
              { img: STILLS.paradecrowd, pos: '50% 50%', title: 'jfk-inauguration-parade.mp4', tin: '00:00:19', tout: '00:00:24', dur: '5s' },
              { img: STILLS.capitolcrowd, pos: '50% 55%', title: 'jfk-inauguration-1961.mp4', tin: '00:00:48', tout: '00:00:52', dur: '4s' },
              { img: STILLS.paradestands, pos: '50% 50%', title: 'jfk-inauguration-parade.mp4', tin: '00:00:10', tout: '00:00:13', dur: '3s' },
            ].map((c, i) => (
              <PanelCard key={i} img={c.img} pos={c.pos} title={c.title} tin={c.tin} tout={c.tout} dur={c.dur} delay={2100 + i * 140} />
            ))}
          </div>
        </GroupBox>
      </Body>
    </PanelDock>
  );
}

function SceneDropTimeline() {
  return (
    <PanelDock>
      <PanelTabs />
      <Body>
        <div style={{ display: 'flex', gap: 7, marginBottom: 4, marginTop: 2 }}>
          {[
            { img: STILLS.moonwalkers, pos: '50% 40%', tin: '00:00:52', tout: '00:01:06', dur: '14s' },
            { img: STILLS.flagplant, pos: '50% 45%', tin: '00:00:28', tout: '00:00:37', dur: '9s' },
            { img: STILLS.moonflag, pos: '50% 40%', tin: '00:01:05', tout: '00:01:14', dur: '9s' },
          ].map((c, i) => (
            <div key={i} style={{ flex: 1, position: 'relative' }}>
              <PanelCard img={c.img} pos={c.pos} title="apollo11-moonwalk-1969.mp4" tin={c.tin} tout={c.tout} dur={c.dur} delay={i * 120} checked={i === 0} hover={i === 0} hot={0} />
              {i === 0 ? <span className="crw-dragghost" style={{ position: 'absolute', inset: 0, borderRadius: 8, pointerEvents: 'none' }}></span> : null}
            </div>
          ))}
        </div>
        <PlacementBar placeLit />
        <div style={{ marginTop: 8 }}>
          <MiniTimeline dropDelay={1100} dropLabel="MOONWALK · 0:52–1:06" />
        </div>
        <div className="crw-fade" style={{ animationDelay: '1900ms', fontFamily: mono, fontSize: 9, color: APP.green, letterSpacing: '0.06em', marginTop: 10 }}>✓ ON TIMELINE — MEDIA RELINKED FROM LOCAL DRIVE</div>
      </Body>
    </PanelDock>
  );
}

export const SCENES = {
  'visual-search': SceneVisualSearch,
  'context-picker': SceneContextPicker,
  'image-search': SceneImageSearch,
  'match-similar': SceneMatchSimilar,
  'speech-search': SceneSpeechSearch,
  'transcript': SceneTranscript,
  'add-timeline': SceneAddTimeline,
  'languages': SceneLanguages,
  'faces-search': SceneFacesSearch,
  'faces-combine': SceneFacesCombine,
  'faces-name': SceneFacesName,
  'faces-manage': SceneFacesManage,
  'ingest': SceneIngest,
  'scope': SceneScope,
  'drop-timeline': SceneDropTimeline,
} satisfies Record<string, () => ReactElement>;

export type SceneName = keyof typeof SCENES;
